import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import Keyboard from "simple-keyboard";
import * as fromApp from "../../store/app.reducer";
import * as selectors from "../../store/app.selectors";

@Component({
  selector: "app-cash-change-calculator-modal",
  templateUrl: "./cash-change-calculator-modal.component.html",
  styleUrls: ["./cash-change-calculator-modal.component.scss"],
})
export class CashChangeCalculatorModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() order: any;
  @ViewChild("simpleKeyboard", { static: false })
  simpleKeyboardRef: ElementRef<HTMLDivElement>;

  public quickAmounts: Array<{ value: number; label: string }> = [
    { value: 10, label: "10.00" },
    { value: 20, label: "20.00" },
    { value: 50, label: "50.00" },
  ];
  public paidAmount: any;
  public paidAmountDisplay: string = null;
  public orderTotal: number = 0;
  public orderTotalDisplay: string = "0.00";
  public changeAmount: number = 0;
  public changeAmountDisplay: string = "0.00";
  public insufficientAmount: boolean = false;
  public showChangeAmount: boolean = false;
  public currencySymbol: string = "€";
  public showPricesWithComma: boolean;
  public storeInfo: any;

  private storeInfoSub: Subscription;
  private unsubscribeBackEvent: Subscription;
  private keyboard: Keyboard | null = null;
  private syncingKeyboardValue = false;

  constructor(
    private modalController: ModalController,
    private store: Store<fromApp.AppState>,
    private platform: Platform
  ) {}

  ngOnInit() {
    this.orderTotal = this.parseAmount(this.order?.grand_total) || 0;
    this.updateFormattedAmounts();
    this.calculateChange();

    this.unsubscribeBackEvent = this.platform.backButton.subscribeWithPriority(
      200,
      () => {
        if (this.platform.is("android")) {
          this.closeModal();
        }
      }
    );

    this.storeInfoSub = this.store
      .select(selectors.getStoreInfo)
      .pipe(distinctUntilChanged())
      .subscribe((storeInfo) => {
        if (storeInfo && !_.isEqual(this.storeInfo, storeInfo)) {
          this.storeInfo = _.cloneDeep(storeInfo);
          this.currencySymbol = this.storeInfo.currencySymbol || "€";
          this.showPricesWithComma = this.storeInfo.showPricesWithComma;
          this.updateFormattedAmounts();
        }
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      import("simple-keyboard").then((KeyboardClass) => {
        const Keyboard = KeyboardClass.default;
        this.keyboard = new Keyboard({
          rootElement: this.simpleKeyboardRef?.nativeElement,
          onChange: (input) => this.onKeyboardChange(input),
          onKeyPress: (button) => this.onKeyboardKeyPress(button),
          maxLength: 8,
          layout: {
            default: ["1 2 3 4 5 6", "7 8 9 . 0 {backspace}"],
          },
          display: {
            "{backspace}": "⌫",
          },
          buttonTheme: [
            {
              class: "hg-height-buttons",
              buttons: "1 2 3 4 5 6 7 8 9 0 . {backspace}",
            },
          ],
        });
      });
    }, 0);
  }

  selectQuickAmount(amount: number) {
    this.paidAmount = this.formatKeyboardAmount(amount);
    this.syncKeyboardInput(this.paidAmount);
    this.calculateChange();
  }

  onKeyboardChange(amount: string): any {
    if (this.syncingKeyboardValue) {
      return;
    }

    const normalizedAmount = this.normalizeKeyboardAmount(amount);
    this.paidAmount = normalizedAmount;

    if (normalizedAmount !== amount) {
      this.syncKeyboardInput(normalizedAmount);
    }

    this.calculateChange();
  }

  onKeyboardKeyPress(_button: string) {}

  calculateChange() {
    const paidAmount = this.parseAmount(this.paidAmount);
    this.updatePaidAmountDisplay();

    if (paidAmount === null) {
      this.insufficientAmount = false;
      this.showChangeAmount = false;
      this.changeAmount = 0;
      this.updateChangeAmountDisplay();
      return;
    }

    if (paidAmount < this.orderTotal) {
      this.insufficientAmount = true;
      this.showChangeAmount = false;
      this.changeAmount = 0;
      this.updateChangeAmountDisplay();
      return;
    }

    this.insufficientAmount = false;
    this.showChangeAmount = true;
    this.changeAmount = this.roundAmount(paidAmount - this.orderTotal);
    this.updateChangeAmountDisplay();
  }

  closeModal() {
    this.modalController.dismiss();
  }

  ngOnDestroy() {
    if (this.unsubscribeBackEvent) {
      this.unsubscribeBackEvent.unsubscribe();
    }
    if (this.storeInfoSub) {
      this.storeInfoSub.unsubscribe();
    }
    if (this.keyboard && this.keyboard.destroy) {
      this.keyboard.destroy();
    }
  }

  private parseAmount(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsedValue = parseFloat(value.toString().replace(",", "."));
    if (Number.isNaN(parsedValue)) {
      return null;
    }

    return parsedValue;
  }

  private roundAmount(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private normalizeKeyboardAmount(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }

    const valueString = value
      .toString()
      .replace(",", ".")
      .replace(/[^\d.]/g, "");
    const parts = valueString.split(".");
    const hasDecimalSeparator = parts.length > 1;
    const integerPart = parts[0].replace(/^0+(?=\d)/, "").slice(0, 5);

    if (!hasDecimalSeparator) {
      return integerPart;
    }

    const decimalPart = parts.slice(1).join("").slice(0, 2);
    return `${integerPart || "0"}.${decimalPart}`;
  }

  private syncKeyboardInput(value: string) {
    if (!this.keyboard) {
      return;
    }

    this.syncingKeyboardValue = true;
    this.keyboard.setInput(value || "");
    this.syncingKeyboardValue = false;
  }

  private updateFormattedAmounts() {
    this.quickAmounts = _.map(this.quickAmounts, (amount) => {
      return {
        value: amount.value,
        label: this.formatDisplayAmount(amount.value),
      };
    });
    this.orderTotalDisplay = this.formatDisplayAmount(this.orderTotal);
    this.updatePaidAmountDisplay();
    this.updateChangeAmountDisplay();
  }

  private updatePaidAmountDisplay() {
    if (
      this.paidAmount === null ||
      this.paidAmount === undefined ||
      this.paidAmount === ""
    ) {
      this.paidAmountDisplay = null;
      return;
    }

    const paidAmountString = this.normalizeKeyboardAmount(this.paidAmount);
    if (!paidAmountString) {
      this.paidAmountDisplay = null;
      return;
    }

    this.paidAmountDisplay = this.showPricesWithComma
      ? paidAmountString.replace(".", ",")
      : paidAmountString;
  }

  private updateChangeAmountDisplay() {
    this.changeAmountDisplay = this.formatDisplayAmount(this.changeAmount);
  }

  private formatKeyboardAmount(value: any): string {
    const amount = this.parseAmount(value) || 0;
    return this.roundAmount(amount).toFixed(2);
  }

  private formatDisplayAmount(value: any): string {
    const amount = this.parseAmount(value) || 0;
    const formattedAmount = this.roundAmount(amount).toFixed(2);
    return this.showPricesWithComma
      ? formattedAmount.replace(".", ",")
      : formattedAmount;
  }
}
